var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var _finalized, _transaction, _chainData;
import { Transaction } from "@iota/iota-sdk/transactions";
import * as isc from "./isc.js";
import { IOTA_TYPE_ARG } from "@iota/iota-sdk/utils";
import { L2_FROM_L1_GAS_BUDGET } from "./constants.js";
class IscTransaction {
  constructor(chainData, transaction = new Transaction()) {
    __privateAdd(this, _finalized);
    __privateAdd(this, _transaction);
    __privateAdd(this, _chainData);
    __privateSet(this, _finalized, false);
    __privateSet(this, _transaction, transaction);
    __privateSet(this, _chainData, chainData);
  }
  validateFinalizedStatus() {
    if (__privateGet(this, _finalized)) {
      throw Error("Transaction is built.");
    }
  }
  /**
   * Getter for the IOTA MOVE Transaction.
   */
  transaction() {
    return __privateGet(this, _transaction);
  }
  /**
   * Create a bag.
   */
  newBag() {
    this.validateFinalizedStatus();
    return isc.newBag(__privateGet(this, _transaction), __privateGet(this, _chainData));
  }
  /**
   * Get some amount in a coin.
   */
  coinFromAmount({ amount }) {
    this.validateFinalizedStatus();
    return isc.coinFromAmount(__privateGet(this, _transaction), BigInt(amount));
  }
  /**
   * Place a coin in a bag.
   *
   * **Uses the IOTA Coin Type by default.**
   */
  placeCoinInBag({
    bag,
    coinType = IOTA_TYPE_ARG,
    coin
  }) {
    this.validateFinalizedStatus();
    isc.placeCoinInBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag, coinType, coin);
  }
  /**
   * Finally create and send a request calling the given `contractFunction` with `contractArgs` in `contract`
   */
  createAndSend({
    bag,
    contract,
    contractFunction,
    contractArgs,
    transfers,
    gasBudget = L2_FROM_L1_GAS_BUDGET
  }) {
    this.validateFinalizedStatus();
    isc.createAndSendRequest(
      __privateGet(this, _transaction),
      __privateGet(this, _chainData),
      contract,
      contractFunction,
      contractArgs,
      bag,
      transfers,
      gasBudget
    );
  }
  /**
   * Finally create and send a request calling the given `accountsFunction` in `accountsContract`
   */
  createAndSendToEvm({
    address,
    accountsContract,
    accountsFunction,
    transfers,
    gasBudget = L2_FROM_L1_GAS_BUDGET,
    bag
  }) {
    const agentID = isc.agentIdForEVM(address);
    this.createAndSend({
      bag,
      gasBudget,
      contract: accountsContract,
      contractFunction: accountsFunction,
      contractArgs: [agentID],
      transfers
    });
  }
  /**
   * Take out the specified amount of coin from the bag.
   *
   * **Uses the IOTA Coin Type by default.**
   */
  takeCoinBalanceFromBag({
    bag,
    coinType = IOTA_TYPE_ARG,
    amount
  }) {
    this.validateFinalizedStatus();
    return isc.takeCoinBalanceFromBag(
      __privateGet(this, _transaction),
      __privateGet(this, _chainData),
      bag,
      coinType,
      amount
    );
  }
  /**
   * Take out all the coin from the bag.
   *
   * **Uses the IOTA Coin Type by default.**
   */
  takeAllCoinBalanceFromBag({
    bag,
    coinType = IOTA_TYPE_ARG
  }) {
    this.validateFinalizedStatus();
    return isc.takeAllCoinBalanceFromBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag, coinType);
  }
  /**
   * Place a coin balance in the bag.
   *
   * **Uses the IOTA Coin Type by default.**
   */
  placeCoinBalanceInBag({
    bag,
    coinType = IOTA_TYPE_ARG,
    balance
  }) {
    this.validateFinalizedStatus();
    isc.placeCoinBalanceInBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag, coinType, balance);
  }
  /**
   * Place an asset in the bag.
   */
  placeAssetInBag({
    bag,
    asset,
    assetType
  }) {
    this.validateFinalizedStatus();
    isc.placeAssetInBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag, assetType, asset);
  }
  /**
   * Take an asset from a bag.
   */
  takeAssetFromBag({
    bag,
    assetType,
    asset
  }) {
    this.validateFinalizedStatus();
    isc.takeAssetFromBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag, assetType, asset);
  }
  /**
   * Get the size of the bag.
   */
  getSizeOfBag({ bag }) {
    this.validateFinalizedStatus();
    return isc.getSizeOfBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag);
  }
  /**
   * Destroy the bag.
   */
  destroyBag({ bag }) {
    this.validateFinalizedStatus();
    return isc.destroyBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag);
  }
  startNewChain({ metadata, coin }) {
    this.validateFinalizedStatus();
    return isc.startNewChain(__privateGet(this, _transaction), __privateGet(this, _chainData), metadata, coin);
  }
  createAnchorWithAssetBag({ bag }) {
    this.validateFinalizedStatus();
    return isc.createAnchorWithAssetBag(__privateGet(this, _transaction), __privateGet(this, _chainData), bag);
  }
  updateAnchorStateForMigraton({
    anchor,
    metadata,
    stateIndex
  }) {
    this.validateFinalizedStatus();
    return isc.updateAnchorStateForMigraton(
      __privateGet(this, _transaction),
      __privateGet(this, _chainData),
      anchor,
      metadata,
      stateIndex
    );
  }
  destroyAnchor({ anchor }) {
    this.validateFinalizedStatus();
    return isc.destroyAnchor(__privateGet(this, _transaction), __privateGet(this, _chainData), anchor);
  }
  borrowAssets({ anchor }) {
    this.validateFinalizedStatus();
    return isc.borrowAssets(__privateGet(this, _transaction), __privateGet(this, _chainData), anchor);
  }
  returnAssetsFromBorrow({
    anchor,
    bag,
    borrow
  }) {
    this.validateFinalizedStatus();
    return isc.returnAssetsFromBorrow(__privateGet(this, _transaction), __privateGet(this, _chainData), anchor, bag, borrow);
  }
  receiveRequest({ anchor, request }) {
    this.validateFinalizedStatus();
    return isc.receiveRequest(__privateGet(this, _transaction), __privateGet(this, _chainData), anchor, request);
  }
  transition({
    anchor,
    newStateMetadata,
    receipts
  }) {
    this.validateFinalizedStatus();
    return isc.transition(
      __privateGet(this, _transaction),
      __privateGet(this, _chainData),
      anchor,
      newStateMetadata,
      receipts
    );
  }
  placeCoinForMigration({
    anchor,
    coinType = IOTA_TYPE_ARG,
    coin
  }) {
    this.validateFinalizedStatus();
    return isc.placeCoinForMigration(
      __privateGet(this, _transaction),
      __privateGet(this, _chainData),
      anchor,
      coinType,
      coin
    );
  }
  placeCoinBalanceForMigration({
    anchor,
    coinType = IOTA_TYPE_ARG,
    balance
  }) {
    this.validateFinalizedStatus();
    return isc.placeCoinBalanceForMigration(
      __privateGet(this, _transaction),
      __privateGet(this, _chainData),
      anchor,
      coinType,
      balance
    );
  }
  placeAssetForMigration({
    anchor,
    assetType,
    asset
  }) {
    this.validateFinalizedStatus();
    return isc.placeAssetForMigration(
      __privateGet(this, _transaction),
      __privateGet(this, _chainData),
      anchor,
      assetType,
      asset
    );
  }
  /**
   * Stop building this ISC Transaction and return the IOTA MOVE Transaction.
   * @returns IOTA MOVE Transaction.
   */
  build() {
    this.validateFinalizedStatus();
    __privateSet(this, _finalized, true);
    return __privateGet(this, _transaction);
  }
}
_finalized = new WeakMap();
_transaction = new WeakMap();
_chainData = new WeakMap();
export {
  IscTransaction
};
//# sourceMappingURL=transaction.js.map
